import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "reducers";
import { concreteBaseService } from "../../services/concreteBase.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IOutputSearch } from "../../domain/IOutputSearch";
import { IOutputListItem } from "../../domain/IOutputListItem";
import { IConcreteBaseItem } from "../../domain/IConcreteBaseItem";
import { IConcreteBaseInputItemUpdate } from "../../domain/IConcreteBaseInputItemUpdate";
import { IOutputForPrint } from "../../domain/IOutputForPrint";
import { IOutputReportForPrint } from "../../domain/IOutputReportForPrint";
import { IGetOutputReportRequestParams } from "../../domain/IGetOutputReportRequestParams";
import { IConcreteBaseOutputUpdate } from "../../domain/IConcreteBaseOutputUpdate";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";

export const getOutputs = createAsyncThunk(
  "ConcreteBase/getOutputs",
  async (params: {
    getOutputListItemsSearchParams: IOutputSearch,
    pagingParams: IGetPageRequestParams,
    abortSignal: AbortSignal,
    updateStateAfterFetch: (
      updateStateParams: IUpdateStateAfterSearchParams
    ) => void
  }, { rejectWithValue }) => {
    try {
      const response = await concreteBaseService.getOutputs(
        params.getOutputListItemsSearchParams,
        params.pagingParams,
        params.abortSignal
      );
      return {
        response: response,
        pagingData: params.pagingParams,
        updateStateAfterFetch: params.updateStateAfterFetch
      }
    } catch (err) {
      setError(err);
    }
  }
);

export const getOwnProductionItems = createAsyncThunk(
  "ConcreteBase/getOwnProductionItems",
  async () => {
    try {
      return await concreteBaseService.getConcreteBaseInputItems(true);
    } catch (err) {
      setError(err);
    }
  }
);
export const updateConcreteBaseOutput = createAsyncThunk(
  "ConcreteBase/updateOutput",
  async (data: IConcreteBaseOutputUpdate, { rejectWithValue }) => {
    try {
      return await concreteBaseService.updateConcreteBaseOutput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);


export const updateConcreteBaseInputItem = createAsyncThunk(
  "ConcreteBase/updateInputItem",
  async (data: IConcreteBaseInputItemUpdate, { rejectWithValue }) => {
    try {
      return await concreteBaseService.updateConcreteBaseInputItem(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const getOutputForPrint = createAsyncThunk(
  "ConcreteBase/getOutputForPrint",
  async (outputId: number) => {
    try {
      const result = await concreteBaseService.getOutputForPrint(
        outputId,
      );
      return result;
    } catch (err) {
      setError(err);
    }
  }
);

export const getOutputReportForPrint = createAsyncThunk(
  "ConcreteBase/getOutputReportForPrint",
  async (params: IGetOutputReportRequestParams) => {
    try {
      const result = await concreteBaseService.getOutputReportForPrint(params);
      return result;
    } catch (err) {
      setError(err);
    }
  }
);


export const deleteConcreteBaseOutput = createAsyncThunk(
  "ConcreteBase/deleteConcreteBaseOutput",
  async (outputId: number, { rejectWithValue }) => {
    try {
      const response = await concreteBaseService.deleteConcreteBaseOutput(outputId);
      return { request: { outputId }, deletedOutputIds: response.deletedOutputIds };
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

interface outputState {
  loading: boolean;
  outputs: IOutputListItem[];
  outputToDelete: IOutputListItem | undefined
  totalQuantityAmount: number;
  totalCost: number;
  ownProductionItems: IConcreteBaseItem[];
  outputForPrint?: IOutputForPrint;
  outputReportForPrint?: IOutputReportForPrint;
  searchPopover: IPopoverState;
  editableItemInventoryPopover: IPopoverState;
  returnPopover: IPopoverState;
  error: object;
}

const initialState: outputState = {
  loading: false,
  outputs: [],
  outputToDelete: undefined,
  totalQuantityAmount: 0,
  totalCost: 0,
  ownProductionItems: [],
  outputForPrint: undefined,
  outputReportForPrint: undefined,
  searchPopover: { isOpen: false },
  editableItemInventoryPopover: { isOpen: false },
  returnPopover: { isOpen: false },
  error: {},
};

export const outputSlice = createSlice({
  name: "output",
  initialState,
  reducers: {
    reset: (state) => {
      state = initialState;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateOutputTemporarily: (
      state,
      action: PayloadAction<IConcreteBaseOutputUpdate>
    ) => {
      for (let inputIndex = 0; inputIndex < state.outputs.length; inputIndex++) {
        if (state.outputs[inputIndex].outputId === action.payload.outputId) {
          if (action.payload.note !== undefined) {
            state.outputs[inputIndex].note = action.payload.note;
          }
        }
      }
    },

    updateInputItemTemporarily: (
      state,
      action: PayloadAction<IConcreteBaseInputItemUpdate>
    ) => {
      for (let itemIndex = 0; itemIndex < state.ownProductionItems.length; itemIndex++) {
        if (state.ownProductionItems[itemIndex].itemId === action.payload.inputItemId) {
          if (action.payload.itemName !== undefined) {
            state.ownProductionItems[itemIndex].name = action.payload.itemName;
          }
          if (action.payload.itemCode !== undefined) {
            state.ownProductionItems[itemIndex].latestItemCode = action.payload.itemCode;
          }
          if (action.payload.pricePerUnit !== undefined) {
            state.ownProductionItems[itemIndex].latestPricePerUnit = action.payload.pricePerUnit;
          }
          if (action.payload.canStayInRepository !== undefined) {
            state.ownProductionItems[itemIndex].canStayInRepository = action.payload.canStayInRepository;
          }
          break;
        }
      }
    },
    addOwnProductionItem: (
      state,
      action: PayloadAction<IConcreteBaseItem>
    ) => {
      state.ownProductionItems.push(action.payload);
    },
    setSearchPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchPopover = action.payload;
    },
    setEditableItemInventoryPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.editableItemInventoryPopover = action.payload;
    },
    setReturnPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.returnPopover = action.payload;
    },
    requestDeletingOutput: (state, action: PayloadAction<IOutputListItem | undefined>) => {
      state.outputToDelete = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOutputs.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getOutputs.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.listItems.length;
          const newListItems = action.payload.response.listItems;
          const newFirstElementId = newListItems?.[0]?.outputItemId;
          const newLastElementId = newListItems?.[newListItems.length - 1]?.outputItemId;
          state.outputs = newListItems;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...action.payload.response.listItems, ...state.outputs];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.outputItemId;
          state.outputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...state.outputs, ...action.payload.response.listItems];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = action.payload.response.listItems?.[elementsFetched - 1]?.outputItemId;
          state.outputs = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }
        state.totalCost = action.payload.response.totalCost;
        state.totalQuantityAmount = action.payload.response.totalQuantityAmount;
      }
    });
    builder.addCase(getOutputs.rejected, (state, action) => {
      state.loading = true;
      state.error = action.error;
    });

    builder.addCase(getOwnProductionItems.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getOwnProductionItems.fulfilled, (state, action) => {
      state.loading = false;
      state.ownProductionItems = action.payload ? action.payload : [];
    });
    builder.addCase(getOwnProductionItems.rejected, (state, action) => {
      state.loading = true;
      state.error = action.error;
    });

    builder.addCase(updateConcreteBaseOutput.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateConcreteBaseOutput.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.outputs = state.outputs.map(existingItem => {
          if (existingItem.outputId === action.payload.outputId) {
            return { ...existingItem, note: action.payload.note };
          } else {
            return existingItem;
          }
        });
      }
    });
    builder.addCase(updateConcreteBaseOutput.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });

    builder.addCase(updateConcreteBaseInputItem.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateConcreteBaseInputItem.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.ownProductionItems = state.ownProductionItems.map(existingItem => {
          if (existingItem.itemId === action.payload.updatedItem.itemId) {
            return action.payload.updatedItem;
          } else {
            return existingItem;
          }
        });
        action.payload.changedOutputListItems.forEach(changedOutput => {
          for (let outputIndex = 0; outputIndex < state.outputs.length; outputIndex++) {
            if (state.outputs[outputIndex].outputItemId === changedOutput.outputItemId) {
              state.outputs[outputIndex] = changedOutput;
            }
          }
        });
      }
    });
    builder.addCase(updateConcreteBaseInputItem.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getOutputForPrint.fulfilled, (state, action) => {
      state.loading = false;
      state.outputForPrint = action.payload ? action.payload : undefined;
    });
    builder.addCase(getOutputReportForPrint.fulfilled, (state, action) => {
      state.loading = false;
      state.outputReportForPrint = action.payload ? action.payload : undefined;
    });
    builder.addCase(deleteConcreteBaseOutput.fulfilled, (state, action) => {
      if (action.payload) {
        state.outputs = state.outputs.filter(outputItem => !action.payload.deletedOutputIds.includes(outputItem.outputId));
      }
    });
  },
});

export const {
  setError,
  setSearchPopover,
  reset,
  updateOutputTemporarily,
  updateInputItemTemporarily,
  addOwnProductionItem,
  setReturnPopover,
  setEditableItemInventoryPopover,
  requestDeletingOutput,
} = outputSlice.actions;

export const selectState = (state: RootState) => state.cb.output;

export default outputSlice.reducer;
